import { useId, useRef, useState, type FormEvent, type ReactNode } from "react";
import { fullName, type CallerNumbersResponse, type OrganizationRole, type TeamInvitation, type TeamMember, type UpdateMembershipBody } from "@gioia365/contracts";
import { isSuperAdminRole } from "@gioia365/domain/access";
import { Link } from "@tanstack/react-router";
import { ApiRequestError } from "../../api/http";
import { useInvite, useResendInvitation, useUpdateMembership, type MembershipTransition } from "../../api/queries";
import { Button } from "../../design/components/Button";
import { Dialog } from "../../design/components/Dialog";
import { missingNamePart, NameFields, type NamePart } from "../../design/components/NameFields";
import { Reason } from "../../design/components/Reason";
import { Select } from "../../design/components/Select";
import { TextField } from "../../design/components/TextField";
import { messageForError } from "../../i18n/errors";
import { useT, type Translate } from "../../i18n/locale";
import { withLink } from "../../i18n/with-link";
import type { TabContext } from "../../session/tab-context";
import { SuspendedLeadsBlock, type LeadsChoice, type ReassignBlock } from "../../team/SuspendedLeadsBlock";
import { CallerNumberSelect, useNumberChoice } from "./caller-numbers";

// Le finestre di Impostazioni → Team (piano M1b Task 7): invito, «Gestisci» di una persona e di un invito,
// conferma di sospensione e archiviazione. Ognuna nasce a ogni apertura (la pagina le monta soltanto quando
// servono), quindi i campi non ricordano la volta prima. Una finestra modale rende inerte la pagina, avvisi
// compresi (D16): ciò che si decide qui e fallisce si spiega qui; le azioni di stato chiudono la finestra e la
// pagina mostra l'avviso. I Lead di chi smette di lavorare (M2 Task 22) stanno nel riquadro «I suoi N Lead»
// (`SuspendedLeadsBlock`), nella conferma di sospensione e archiviazione e in «Gestisci» di chi è già sospeso; senza i
// conteggi del server, lì si legge il perché, con «Riprova». Il «Numero per chiamare» di una persona (M4 Task 17) si
// sceglie in «Gestisci» fra i cellulari verificati liberi del parco, con la sezione «Numeri per chiamare» e la stessa scelta
// della finestra del solo numero (`useNumberChoice` di `caller-numbers.tsx`).

/** I rifiuti che il Team spiega meglio del testo generico di `errors`. */
const TEAM_ERRORS = ["role_level", "protected_owner", "cannot_act_on_self", "invalid_state", "system_role", "permission_denied", "owner_not_eligible", "membership_from_close"] as const;

/** Il testo di un errore del Team, dal codice; `email` serve ai rifiuti di un invito. */
export function teamErrorMessage(t: Translate, error: unknown, params: { organization: string; email?: string }): string {
  if (error instanceof ApiRequestError) {
    const email = params.email ?? "";
    if (error.code === "already_member") return t("team.errors.already_member", { email, organization: params.organization });
    if (error.code === "invitation_pending") return t("team.errors.invitation_pending", { email });
    // L'archiviazione rifiutata perché la persona ha ancora dei Lead: il server dice quanti.
    const leads = error.details?.leads;
    if (error.code === "membership_has_leads" && typeof leads === "number") return t("team.errors.membership_has_leads", { leads });
    const code = TEAM_ERRORS.find((candidate) => candidate === error.code);
    if (code) return t(`team.errors.${code}`);
  }
  return messageForError(t, error);
}

/** I campi di cui il server dice che non vanno (`422 validation_failed`, `details.fields`). */
function invalidFields(error: unknown): string[] {
  if (!(error instanceof ApiRequestError) || error.code !== "validation_failed") return [];
  const fields = error.details?.fields;
  return Array.isArray(fields) ? fields.filter((field): field is string => typeof field === "string") : [];
}

// Basta a riconoscere uno sbaglio di battitura; l'email vera la controlla il server.
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Un problema del modulo, con il testo calcolato quando si mostra: segue la lingua dell'interfaccia. */
type Problem<Field extends string> = { field: Field; text: (t: Translate) => string };

/**
 * Nome e cognome hanno un solo errore, «Scrivi nome e cognome.», letto sulla casella che manca; se il server rifiuta
 * una parte che c'è, su quella che rifiuta (`details.fields`), o su tutte e due.
 */
type NameProblem = { field: "name"; part: NamePart | "both"; text: (t: Translate) => string };

function nameProblem(part: NamePart | "both"): NameProblem {
  return { field: "name", part, text: (tr) => tr("common.nameMissing") };
}

/** La casella del nome che il server ha rifiutato, se c'è: una sola, oppure tutte e due. */
function rejectedNamePart(fields: readonly string[]): NamePart | "both" | null {
  const first = fields.includes("firstName");
  const last = fields.includes("lastName");
  if (first && last) return "both";
  return first ? "firstName" : last ? "lastName" : null;
}

/** «Cosa può fare ogni ruolo si sceglie in Ruoli e permessi.», con il link alla sezione. */
function RoleHint() {
  const t = useT();
  return withLink(t("team.fields.roleHint"), <Link to="/impostazioni/ruoli-e-permessi">{t("settings.roles")}</Link>);
}

function roleOptions(roles: readonly OrganizationRole[]) {
  return roles.map((role) => ({ value: role.id, label: role.name }));
}

type InviteProps = {
  tab: TabContext;
  organization: string;
  /** I ruoli che chi invita può dare (`assignableRoles`). */
  roles: readonly OrganizationRole[];
  onClose: () => void;
  /** Il server non ha mandato l'email: la pagina mostra il link da copiare. */
  onManual: (name: string, link: string) => void;
  onSent: (name: string, email: string) => void;
  /**
   * L'invito di una persona nata sospesa da Close (INGR-15, «Invita» di «Gestisci»): nome, email e ruolo già scritti, e
   * l'email bloccata, perché l'invito riattiva la sua appartenenza soltanto con la stessa email.
   */
  from?: TeamMember;
};

export function InviteDialog({ tab, organization, roles, onClose, onManual, onSent, from }: InviteProps) {
  const t = useT();
  const formId = useId();
  const invite = useInvite(tab);
  // Di partenza il ruolo di livello più basso: il meno potente fra quelli che si possono dare.
  const lowest = roles.reduce<OrganizationRole | undefined>((best, role) => (!best || role.level < best.level ? role : best), undefined);
  const [firstName, setFirstName] = useState(from?.firstName ?? "");
  const [lastName, setLastName] = useState(from?.lastName ?? "");
  const [email, setEmail] = useState(from?.email ?? "");
  const [roleId, setRoleId] = useState(from && roles.some((role) => role.id === from.role.id) ? from.role.id : (lowest?.id ?? ""));
  const [problem, setProblem] = useState<NameProblem | Problem<"email" | "form"> | null>(null);
  const firstNameRef = useRef<HTMLInputElement>(null);
  const lastNameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const submitRef = useRef<HTMLButtonElement>(null);

  function failName(part: NamePart | "both") {
    setProblem(nameProblem(part));
    (part === "lastName" ? lastNameRef : firstNameRef).current?.focus();
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (invite.isPending || !roleId) return;
    const first = firstName.trim();
    const last = lastName.trim();
    const address = email.trim();
    const missing = missingNamePart(first, last);
    if (missing) {
      failName(missing);
      return;
    }
    if (!EMAIL_SHAPE.test(address)) {
      setProblem({ field: "email", text: (tr) => tr("team.fields.emailInvalid") });
      emailRef.current?.focus();
      return;
    }
    setProblem(null);
    // Il nome intero serve soltanto ai messaggi di qui: il server lo ricava da nome e cognome.
    const name = fullName(first, last);
    try {
      const issued = await invite.mutateAsync({ firstName: first, lastName: last, email: address, roleId });
      if (issued.manualDelivery) onManual(name, issued.manualDelivery.link);
      else onSent(name, address);
    } catch (error) {
      const fields = invalidFields(error);
      const code = error instanceof ApiRequestError ? error.code : null;
      const rejectedName = rejectedNamePart(fields);
      if (code === "already_member" || code === "invitation_pending" || fields.includes("email")) {
        setProblem({
          field: "email",
          text: (tr) => (fields.includes("email") ? tr("team.fields.emailInvalid") : teamErrorMessage(tr, error, { organization, email: address })),
        });
        emailRef.current?.focus();
      } else if (rejectedName) {
        failName(rejectedName);
      } else {
        setProblem({ field: "form", text: (tr) => teamErrorMessage(tr, error, { organization, email: address }) });
        submitRef.current?.focus();
      }
    }
  }

  const errorOf = (field: "name" | "email" | "form") => (problem?.field === field ? problem.text(t) : undefined);
  const formError = errorOf("form");

  return (
    <Dialog
      open
      title={t("team.invite")}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{t("common.cancel")}</Button>
          <Button
            ref={submitRef}
            variant="primary"
            type="submit"
            form={formId}
            disabled={invite.isPending || roles.length === 0}
            aria-busy={invite.isPending ? "true" : undefined}
          >
            {t("team.inviteDialog.submit")}
          </Button>
        </>
      }
    >
      <form id={formId} className="g-stack" onSubmit={(event) => void handleSubmit(event)} aria-busy={invite.isPending} noValidate>
        <NameFields
          id="team-invite-name"
          firstName={{ label: t("common.firstName"), value: firstName, onChange: setFirstName, ref: firstNameRef }}
          lastName={{ label: t("common.lastName"), value: lastName, onChange: setLastName, ref: lastNameRef }}
          error={errorOf("name")}
          invalid={problem?.field === "name" ? problem.part : undefined}
        />
        <TextField
          id="team-invite-email"
          ref={emailRef}
          label={t("team.fields.email")}
          type="email"
          value={email}
          maxLength={254}
          autoComplete="off"
          hint={t(from ? "team.fields.emailFromClose" : "team.fields.emailHint")}
          error={errorOf("email")}
          readOnly={from !== undefined}
          onChange={(event) => setEmail(event.target.value)}
        />
        {roles.length > 0 ? (
          <Select id="team-invite-role" label={t("team.fields.role")} value={roleId} onChange={setRoleId} options={roleOptions(roles)} hint={<RoleHint />} />
        ) : (
          <Reason tone="warning">{t("team.fields.noRoles")}</Reason>
        )}
        {formError ? (
          <p className="g-field__error" role="alert">
            {formError}
          </p>
        ) : null}
      </form>
    </Dialog>
  );
}

type MemberProps = {
  tab: TabContext;
  member: TeamMember;
  organization: string;
  /** I ruoli che chi gestisce può dare; se quello attuale non c'è, il ruolo resta in sola lettura. */
  roles: readonly OrganizationRole[];
  /** I Lead non archiviati della persona, dai conteggi del server; `null` se i conteggi non si sono letti. */
  leads: number | null;
  /** I conteggi non si sono letti: per chi è sospeso o archiviato la finestra lo dice, con «Riprova». */
  countsFailed?: { retry: () => void; retrying: boolean };
  /** `null` se chi gestisce può riassegnarli, altrimenti il perché (vedi `SuspendedLeadsBlock`). */
  reassignBlock: ReassignBlock | null;
  /** Il parco dei cellulari, se il server lo lascia leggere: allora c'è la scelta «Numero per chiamare». */
  pool: CallerNumbersResponse | null;
  onClose: () => void;
  onSaved: (name: string) => void;
  onStatus: (action: MembershipTransition) => void;
  /** «Riassegnali agli altri User, a turno» per chi è già sospeso o archiviato: la pagina chiude e lo dice. */
  onReassign: () => void;
  /** «Invita» di una persona nata sospesa da Close (INGR-15): la pagina apre l'invito con i suoi dati. */
  onInvite: () => void;
};

/**
 * «Gestisci» di una persona: nome e cognome nell'azienda, ruolo e «Numero per chiamare»; sospendere, riattivare,
 * archiviare. Si manda soltanto ciò che cambia, anche una sola parte del nome: il server ricalcola il nome intero. Chi è
 * sospeso o archiviato con dei Lead ancora suoi ha qui «Riassegnali agli altri User, a turno».
 *
 * Il numero (M4 Task 17) si salva dopo nome e ruolo, e soltanto se cambia, con `useNumberChoice` (la stessa scelta della
 * finestra del solo numero): prima si libera il suo e poi si dà il nuovo, ognuno con la sua versione. Se il server rifiuta il
 * parco si rilegge, la scelta riparte da ciò che il server ha adesso e la finestra resta aperta con il perché; ciò che si era
 * già salvato non si manda di nuovo.
 */
export function MemberDialog({ tab, member, organization, roles, leads, countsFailed, reassignBlock, pool, onClose, onSaved, onStatus, onReassign, onInvite }: MemberProps) {
  const t = useT();
  const formId = useId();
  const update = useUpdateMembership(tab);
  const number = useNumberChoice(tab, pool, member);
  // Una riga nata prima dei due campi con un nome di una parola ha il cognome vuoto: va scritto soltanto se si
  // cambia il nome; un cambio di solo ruolo si salva com'è.
  const [firstName, setFirstName] = useState(member.firstName);
  const [lastName, setLastName] = useState(member.lastName);
  const [roleId, setRoleId] = useState(member.role.id);
  // Nome e ruolo come li ha il server: all'apertura quelli della riga, dopo un salvataggio riuscito quelli salvati.
  const [saved, setSaved] = useState({ firstName: member.firstName, lastName: member.lastName, roleId: member.role.id });
  const [problem, setProblem] = useState<NameProblem | Problem<"form"> | null>(null);
  const firstNameRef = useRef<HTMLInputElement>(null);
  const lastNameRef = useRef<HTMLInputElement>(null);
  const submitRef = useRef<HTMLButtonElement>(null);
  const roleEditable = roles.some((role) => role.id === member.role.id);
  // Perché il ruolo resta in sola lettura: un ruolo di livello Super admin non si dà a nessuno (il Super admin è
  // uno solo, questione 38); un altro ruolo, perché può fare cose che chi gestisce non può fare.
  const readOnlyRoleHint = isSuperAdminRole(member.role) ? "team.fields.roleSuperAdminLevel" : "team.fields.roleReadOnly";

  function failName(part: NamePart | "both") {
    setProblem(nameProblem(part));
    (part === "lastName" ? lastNameRef : firstNameRef).current?.focus();
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (update.isPending || number.pending) return;
    const first = firstName.trim();
    const last = lastName.trim();
    const nameTouched = first !== saved.firstName || last !== saved.lastName;
    const missing = nameTouched ? missingNamePart(first, last) : null;
    if (missing) {
      failName(missing);
      return;
    }
    setProblem(null);
    number.clearProblem();
    // Soltanto ciò che cambia; se non cambia nulla non c'è niente da salvare.
    const body: UpdateMembershipBody = {
      ...(first !== saved.firstName ? { firstName: first } : {}),
      ...(last !== saved.lastName ? { lastName: last } : {}),
      ...(roleEditable && roleId !== saved.roleId ? { roleId } : {}),
    };
    const numberChanged = number.changed;
    if (Object.keys(body).length === 0 && !numberChanged) {
      onClose();
      return;
    }
    if (Object.keys(body).length > 0) {
      try {
        await update.mutateAsync({ membershipId: member.membershipId, body });
        setSaved({ firstName: first, lastName: last, roleId: body.roleId ?? saved.roleId });
      } catch (error) {
        const rejectedName = rejectedNamePart(invalidFields(error));
        if (rejectedName) {
          failName(rejectedName);
        } else {
          setProblem({ field: "form", text: (tr) => teamErrorMessage(tr, error, { organization }) });
          submitRef.current?.focus();
        }
        return;
      }
    }
    const name = fullName(first, last);
    if (numberChanged && !(await number.save(name))) return;
    onSaved(name);
  }

  const formError = problem?.field === "form" ? problem.text(t) : undefined;
  const busy = update.isPending || number.pending;

  return (
    <Dialog
      open
      size="wide"
      title={member.displayName}
      onClose={onClose}
      footer={
        <>
          {member.status === "active" ? (
            <Button variant="ghost" onClick={() => onStatus("suspend")} disabled={busy}>{t("team.manageDialog.suspend")}</Button>
          ) : null}
          {member.status === "suspended" ? (
            <>
              {/* Chi è nato sospeso da Close torna soltanto con un invito che accetta lui (INGR-15): niente «Riattiva». */}
              {member.fromClose === true ? (
                <Button variant="ghost" onClick={onInvite} disabled={busy}>{t("team.manageDialog.invite")}</Button>
              ) : (
                <Button variant="ghost" onClick={() => onStatus("resume")} disabled={busy}>{t("team.manageDialog.resume")}</Button>
              )}
              <Button variant="ghost" onClick={() => onStatus("archive")} disabled={busy}>{t("team.manageDialog.archive")}</Button>
            </>
          ) : null}
          <span className="g-team__spacer" />
          <Button onClick={onClose}>{t("common.cancel")}</Button>
          <Button ref={submitRef} variant="primary" type="submit" form={formId} disabled={busy} aria-busy={busy ? "true" : undefined}>
            {t("team.manageDialog.save")}
          </Button>
        </>
      }
    >
      <form id={formId} className="g-stack" onSubmit={(event) => void handleSubmit(event)} aria-busy={busy} noValidate>
        <NameFields
          id="team-member-name"
          firstName={{ label: t("common.firstName"), value: firstName, onChange: setFirstName, ref: firstNameRef }}
          lastName={{ label: t("common.lastName"), value: lastName, onChange: setLastName, ref: lastNameRef }}
          error={problem?.field === "name" ? problem.text(t) : undefined}
          invalid={problem?.field === "name" ? problem.part : undefined}
        />
        <TextField id="team-member-email" label={t("team.fields.email")} value={member.email} readOnly hint={t("team.fields.emailLocked")} />
        {roleEditable ? (
          <Select id="team-member-role" label={t("team.fields.role")} value={roleId} onChange={setRoleId} options={roleOptions(roles)} hint={<RoleHint />} />
        ) : (
          <TextField id="team-member-role" label={t("team.fields.role")} value={member.role.name} readOnly hint={t(readOnlyRoleHint)} />
        )}
        {number.shown ? <CallerNumberSelect id="team-member-number" choice={number} /> : null}
        {formError ? (
          <p className="g-field__error" role="alert">
            {formError}
          </p>
        ) : null}
      </form>
      {/* Senza conteggi i Lead di chi non lavora più non spariscono in silenzio: il perché e «Riprova». */}
      {member.status !== "active" && leads === null ? (
        <div className="g-team__problem">
          <Reason tone="warning" role="alert">
            {t("team.leads.countsFailedPending")}
          </Reason>
          {countsFailed ? (
            <Button onClick={countsFailed.retry} disabled={countsFailed.retrying} aria-busy={countsFailed.retrying ? "true" : undefined}>
              {t("common.retry")}
            </Button>
          ) : null}
        </div>
      ) : null}
      {member.status !== "active" && leads !== null && leads > 0 ? (
        <SuspendedLeadsBlock mode="pending" leads={leads} blocked={reassignBlock} busy={busy} onReassign={onReassign} />
      ) : null}
    </Dialog>
  );
}

type InvitationProps = {
  tab: TabContext;
  invitation: TeamInvitation;
  /** Se chi gestisce può dare il ruolo dell'invito (livello e poteri, questione 38); annullarlo chiede soltanto il livello. */
  canResend: boolean;
  organization: string;
  onClose: () => void;
  /** Reinvio consegnato dal server: l'invito nuovo (con un id nuovo) prende il posto del vecchio. */
  onResent: (renewed: TeamInvitation) => void;
  onManual: (name: string, link: string) => void;
  onCancel: () => void;
};

/** «Gestisci» di un invito in attesa: si manda di nuovo o si annulla; l'invito del titolare non si annulla. */
export function InvitationDialog({ tab, invitation, canResend, organization, onClose, onResent, onManual, onCancel }: InvitationProps) {
  const t = useT();
  const resendReasonId = useId();
  const resend = useResendInvitation(tab);
  const [resent, setResent] = useState(false);
  // Durante il reinvio la finestra resta aperta (Chiudi, crocetta, Esc, clic sul fondo): la risposta, con un
  // link da copiare o l'invito nuovo, ha bisogno della finestra che l'ha chiesta.
  const busy = resend.isPending;

  async function handleResend() {
    if (resend.isPending || !canResend) return;
    setResent(false);
    try {
      const issued = await resend.mutateAsync(invitation.invitationId);
      if (issued.manualDelivery) {
        onManual(invitation.displayName, issued.manualDelivery.link);
        return;
      }
      setResent(true);
      onResent({ ...invitation, invitationId: issued.invitation.id, createdAt: issued.invitation.createdAt, expiresAt: issued.invitation.expiresAt });
    } catch {
      // L'errore resta nella mutation e si legge nella finestra.
    }
  }

  return (
    <Dialog
      open
      size="wide"
      title={invitation.displayName}
      onClose={() => {
        if (!busy) onClose();
      }}
      footer={
        <>
          <Button
            variant="ghost"
            onClick={() => void handleResend()}
            disabled={busy || !canResend}
            aria-busy={busy ? "true" : undefined}
            aria-describedby={canResend ? undefined : resendReasonId}
          >
            {t("team.invitationDialog.resend")}
          </Button>
          {invitation.isProtectedOwner ? null : (
            <Button variant="ghost" onClick={onCancel} disabled={busy}>{t("team.invitationDialog.cancel")}</Button>
          )}
          <span className="g-team__spacer" />
          <Button onClick={onClose} disabled={busy}>{t("common.close")}</Button>
        </>
      }
    >
      <div className="g-stack">
        <NameFields
          id="team-invitation-name"
          firstName={{ label: t("common.firstName"), value: invitation.firstName }}
          lastName={{ label: t("common.lastName"), value: invitation.lastName }}
          readOnly
        />
        <TextField id="team-invitation-email" label={t("team.fields.email")} value={invitation.email} readOnly />
        <TextField id="team-invitation-role" label={t("team.fields.role")} value={invitation.role.name} readOnly />
        {/* Il motivo del reinvio spento resta scritto, non soltanto nel pulsante (UX_SYSTEM). */}
        {canResend ? null : <Reason id={resendReasonId}>{t("team.invitationDialog.resendBlocked")}</Reason>}
        {resend.isError ? (
          <p className="g-field__error" role="alert">
            {teamErrorMessage(t, resend.error, { organization, email: invitation.email })}
          </p>
        ) : null}
        <p className="g-sheet__status" role="status">
          {resent ? t("team.invitationDialog.resent", { email: invitation.email }) : null}
        </p>
      </div>
    </Dialog>
  );
}

type ConfirmProps = {
  action: "suspend" | "archive";
  name: string;
  organization: string;
  /** I Lead non archiviati della persona; `null` mentre il server li conta, zero se non ne ha o se non si leggono. */
  leads: number | null;
  /**
   * I conteggi non si sono letti: la finestra lo dice, con «Riprova», invece di fare come se la persona non avesse Lead.
   * Confermare resta possibile (una sospensione non deve dipendere dai conteggi): il server decide come sempre.
   */
  countsFailed?: { retry: () => void; retrying: boolean };
  /** `null` se chi conferma può riassegnarli, altrimenti il perché (vedi `SuspendedLeadsBlock`). */
  reassignBlock: ReassignBlock | null;
  onCancel: () => void;
  /** `reassign`: prima della sospensione o dell'archiviazione i Lead si riassegnano a turno (la pagina li mette in fila). */
  onConfirm: (choice: LeadsChoice) => void;
};

/**
 * La conferma prima di sospendere o archiviare; riattivare e annullare un invito agiscono subito. Con dei Lead, la
 * sospensione chiede che cosa farne (di partenza riassegnarli, se si può); l'archiviazione li riassegna prima, perché un
 * Lead non resta a una persona archiviata (questione aperta 75), e senza il potere di farlo aspetta.
 */
export function ConfirmStatusDialog({ action, name, organization, leads, countsFailed, reassignBlock, onCancel, onConfirm }: ConfirmProps) {
  const t = useT();
  const blockedReasonId = useId();
  const canReassign = reassignBlock === null;
  const [choice, setChoice] = useState<LeadsChoice>(canReassign ? "reassign" : "keep");
  const suspend = action === "suspend";
  const counting = leads === null && !countsFailed;
  const owned = leads ?? 0;
  const archiveBlocked = !suspend && owned > 0 && !canReassign;

  let confirmLabel = suspend ? t("team.manageDialog.suspend") : t("team.manageDialog.archive");
  if (!suspend && owned > 0 && canReassign) confirmLabel = t("team.confirm.archiveReassign");

  let leadsPart: ReactNode = null;
  if (countsFailed) {
    leadsPart = (
      <div className="g-team__problem">
        <Reason tone="warning" role="alert">
          {suspend ? t("team.leads.countsFailedSuspend") : t("team.leads.countsFailedArchive")}
        </Reason>
        <Button onClick={countsFailed.retry} disabled={countsFailed.retrying} aria-busy={countsFailed.retrying ? "true" : undefined}>
          {t("common.retry")}
        </Button>
      </div>
    );
  } else if (counting) {
    leadsPart = (
      <p className="g-sheet__status" role="status">
        {t("team.leads.counting")}
      </p>
    );
  } else if (owned > 0 && suspend) {
    leadsPart = <SuspendedLeadsBlock mode="choose" leads={owned} blocked={reassignBlock} choice={choice} onChoice={setChoice} />;
  } else if (owned > 0 && canReassign) {
    leadsPart = <p>{owned === 1 ? t("team.confirm.archiveLeadsOne", { name, organization }) : t("team.confirm.archiveLeads", { count: owned, name, organization })}</p>;
  } else if (owned > 0) {
    leadsPart = (
      <Reason id={blockedReasonId} tone="warning">
        {reassignBlock === "view"
          ? owned === 1
            ? t("team.confirm.archiveBlockedInViewOne")
            : t("team.confirm.archiveBlockedInView", { count: owned })
          : owned === 1
            ? t("team.confirm.archiveBlockedOne")
            : t("team.confirm.archiveBlocked", { count: owned })}
      </Reason>
    );
  }

  return (
    <Dialog
      open
      title={suspend ? t("team.confirm.suspendTitle", { name }) : t("team.confirm.archiveTitle", { name })}
      onClose={onCancel}
      footer={
        <>
          <Button onClick={onCancel}>{t("common.cancel")}</Button>
          <Button
            variant="primary"
            disabled={counting || archiveBlocked}
            aria-describedby={archiveBlocked ? blockedReasonId : undefined}
            onClick={() => onConfirm(owned > 0 && canReassign && (!suspend || choice === "reassign") ? "reassign" : "keep")}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="g-stack">
        <p>{suspend ? t("team.confirm.suspendText", { name }) : t("team.confirm.archiveText", { name, organization })}</p>
        {leadsPart}
      </div>
    </Dialog>
  );
}
